// Directory of navigable Control Panel pages for global search, grouped and
// ordered like the sidebar menu (sidebar/sidebarMenu.tsx). Gates mirror the
// sidebar: a page is only offered when the user passes the same
// permission/feature checks. Keep this file in sync when sidebar routes change.
import { LucideIconName } from 'utils/lucideIcon'

export interface DirectoryPage {
    title: string
    keywords: string[]
    iconName: LucideIconName
    path: (accountSlug: string) => string
    // any-of; omit for pages every user can open
    permissions?: string[]
    feature?: string
    hideForReseller?: boolean
    // Row hint override, e.g. 'Action' for command entries. Defaults to
    // 'Page' / 'Filtered view' derived from the path.
    hint?: string
}

export interface PageResult {
    title: string
    iconName: LucideIconName
    path: string
    // Human summary of an applied scope for parameterized views (e.g.
    // "Visiting today · Canceled"). Plain pages leave it unset.
    subtitle?: string
    hint?: string
}

const accountPage =
    (path: string) =>
    (accountSlug: string): string =>
        `/account/${accountSlug}${path}`

const reportPage = (
    title: string,
    subPath: string,
    feature: string,
    iconName: LucideIconName,
    keywords: string[],
): DirectoryPage => ({
    title,
    keywords: ['reports', ...keywords],
    iconName,
    path: accountPage(`/reports/${subPath}`),
    permissions: ['access_analytics'],
    feature,
})

const DASHBOARD_PAGES: DirectoryPage[] = [
    {
        title: 'Dashboard',
        keywords: ['home', 'overview', 'metrics'],
        iconName: 'Home',
        path: accountPage('/dashboard/overview/'),
    },
]

const ORDER_PAGES: DirectoryPage[] = [
    {
        title: 'Orders',
        keywords: ['order list', 'transactions', 'bookings', 'sales'],
        iconName: 'ShoppingBag',
        path: accountPage('/orders/orders/'),
        permissions: ['access_orders'],
    },
    // Quick scopes: discoverable filtered views of the orders list. Typed
    // scope queries ("orders canceled paypal") are handled by scopedSearch.ts;
    // these entries surface the common ones for single-word queries.
    {
        title: 'Orders · Visiting today',
        keywords: ['orders today', 'today', 'visiting today', 'visits'],
        iconName: 'ShoppingBag',
        path: accountPage('/orders/orders/?dateRange=today&dateRangeType=visit&viaSearch=Visiting+today'),
        permissions: ['access_orders'],
    },
    {
        title: 'Orders · Visiting this week',
        keywords: ['orders this week', 'this week'],
        iconName: 'ShoppingBag',
        path: accountPage('/orders/orders/?dateRange=thisWeek&dateRangeType=visit&viaSearch=Visiting+this+week'),
        permissions: ['access_orders'],
    },
    {
        title: 'Orders · Cancelled',
        keywords: ['canceled orders', 'canceled', 'cancelled', 'cancellations'],
        iconName: 'ShoppingBag',
        path: accountPage(
            '/orders/orders/?dateRange=last30days&dateRangeType=visit&filter=status%3ACANCELED&viaSearch=Visiting+last+30+days+%C2%B7+Cancelled',
        ),
        permissions: ['access_orders'],
    },
    {
        title: 'Orders · Open',
        keywords: ['open orders', 'unpaid orders', 'pending payment'],
        iconName: 'ShoppingBag',
        path: accountPage(
            '/orders/orders/?dateRange=last30days&dateRangeType=visit&filter=status%3AOPEN&viaSearch=Visiting+last+30+days+%C2%B7+Open',
        ),
        permissions: ['access_orders'],
    },
    // Worklist for typo'd customer emails: confirmations that never arrived.
    {
        title: 'Orders · Email bounced',
        keywords: ['bounced', 'bounced email', 'email bounced', 'undelivered', 'wrong email', 'typo email'],
        iconName: 'MailWarning',
        path: accountPage(
            '/orders/orders/?dateRange=last30days&dateRangeType=visit&filter=email_status%3Abounced&viaSearch=Visiting+last+30+days+%C2%B7+Bounced',
        ),
        permissions: ['access_orders'],
    },
    {
        title: 'Daily Schedule',
        keywords: ['schedule', 'visits', 'today'],
        iconName: 'CalendarDays',
        path: accountPage('/orders/daily_schedule/'),
        permissions: ['access_orders'],
        feature: 'dailySchedule',
    },
    {
        title: 'Capacity Report',
        keywords: ['capacity', 'occupancy'],
        iconName: 'Gauge',
        path: accountPage('/orders/capacity_report/'),
        permissions: ['access_orders'],
        feature: 'is_capacity_report_enabled',
    },
    {
        title: 'Pending Reservations',
        keywords: ['carts', 'open carts', 'reservations', 'pay later', 'group bookings', 'group booking'],
        iconName: 'ShoppingCart',
        path: accountPage('/orders/pending-reservations'),
        permissions: ['access_orders'],
        feature: 'openCartView',
    },
    // "Group bookings" in venue language: live pending reservations.
    {
        title: 'Pending Reservations · Group bookings',
        keywords: ['group bookings', 'group booking', 'groups', 'open reservations'],
        iconName: 'Users',
        path: accountPage(
            '/orders/pending-reservations?filter=reservation_status%3Aopen%2Cpending%2Cinitiated&viaSearch=Open+%C2%B7+Pending+%C2%B7+Initiated',
        ),
        permissions: ['access_orders'],
        feature: 'openCartView',
    },
    {
        title: 'Order Disputes',
        keywords: ['paypal', 'disputes', 'chargebacks'],
        iconName: 'ShieldAlert',
        path: accountPage('/orders/paypal/disputes/active'),
        permissions: ['partner_admin'],
        feature: 'PaypalResolutionCenter',
    },
    {
        title: 'Channels',
        keywords: ['resellers', 'sales channels', 'distribution'],
        iconName: 'Share2',
        path: accountPage('/orders/channels/'),
        permissions: ['partner_admin'],
    },
]

const PRODUCT_PAGES: DirectoryPage[] = [
    {
        title: 'Products',
        keywords: ['catalog', 'articles', 'tickets', 'product lists', 'categories'],
        iconName: 'Tickets',
        path: accountPage('/products/home/nested'),
        permissions: ['view_pricing_settings', 'view_product_validity'],
        hideForReseller: true,
    },
    {
        title: 'Pricing Exceptions',
        keywords: ['pricing', 'price override', 'exceptions'],
        iconName: 'BadgePercent',
        path: accountPage('/products/pricing/exceptions/list'),
        permissions: ['view_pricing_settings'],
        feature: 'Pricing',
    },
    {
        title: 'Validity Exceptions',
        keywords: ['validity', 'exceptions', 'ticket validity'],
        iconName: 'CalendarX',
        path: accountPage('/products/validity/exceptions/list'),
        permissions: ['view_product_validity'],
    },
    {
        title: 'Time Slots',
        keywords: ['timeslots', 'sessions'],
        iconName: 'Clock',
        path: accountPage('/products/time_slots/home/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Crowd Control',
        keywords: ['inventory', 'availability'],
        iconName: 'SlidersHorizontal',
        path: accountPage('/products/inventory/home/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'APF Studio',
        keywords: ['apf', 'product form', 'studio'],
        iconName: 'LayoutTemplate',
        path: accountPage('/products/apf_studio/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Discount Rules',
        keywords: ['discounts', 'rules', 'automatic discounts'],
        iconName: 'Percent',
        path: accountPage('/products/discount_rules/home/'),
        permissions: ['partner_admin'],
        feature: 'DiscountRulesPage',
    },
]

const VENUE_PAGES: DirectoryPage[] = [
    {
        title: 'Opening Times',
        keywords: ['venue', 'opening hours'],
        iconName: 'CalendarClock',
        path: accountPage('/venue/opening_times/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Codes',
        keywords: ['booking codes', 'reservations', 'venue codes'],
        iconName: 'QrCode',
        path: accountPage('/venue/codes'),
        permissions: ['access_codes'],
    },
    {
        title: 'Park Map',
        keywords: ['venue map'],
        iconName: 'Map',
        path: accountPage('/venue/park_map'),
        feature: 'parkMap',
    },
]

const SALES_PAGES: DirectoryPage[] = [
    {
        title: 'Point of Sale',
        keywords: ['pos', 'terminals', 'kiosk'],
        iconName: 'Monitor',
        path: accountPage('/pos/'),
        hideForReseller: true,
    },
    {
        title: 'Memberships',
        keywords: ['members', 'season pass', 'subscriptions'],
        iconName: 'IdCard',
        path: accountPage('/memberships/'),
        permissions: ['access_orders'],
        feature: 'memberships',
        hideForReseller: true,
    },
    // Quick scopes: discoverable filtered membership views; typed scope
    // queries ("members overdue") are handled by scopedSearch.ts.
    {
        title: 'Memberships · Active',
        keywords: ['active members', 'active memberships'],
        iconName: 'IdCard',
        path: accountPage('/memberships/?filter=membership_status%3Aactive&viaSearch=Active'),
        permissions: ['access_orders'],
        feature: 'memberships',
        hideForReseller: true,
    },
    {
        title: 'Memberships · Overdue',
        keywords: ['overdue', 'overdue members', 'payment overdue', 'overdue memberships'],
        iconName: 'IdCard',
        path: accountPage('/memberships/?filter=membership_status%3Aoverdue&viaSearch=Overdue'),
        permissions: ['access_orders'],
        feature: 'memberships',
        hideForReseller: true,
    },
    {
        title: 'Dynamic Pricing',
        keywords: ['pricing settings', 'price'],
        iconName: 'Sparkles',
        path: accountPage('/global_pricing_settings'),
        permissions: ['partner_admin'],
        feature: 'pricingMenu',
    },
    {
        title: 'Discounts',
        keywords: ['discount codes', 'pools', 'vouchers', 'coupons'],
        iconName: 'Tag',
        path: accountPage('/discounts/pools'),
        permissions: ['access_codes'],
        feature: 'discountCodes',
    },
]

const ENGAGE_PAGES: DirectoryPage[] = [
    {
        title: 'Engage Tools',
        keywords: ['wonderbar', 'checkout', 'engage'],
        iconName: 'Megaphone',
        path: accountPage('/engage/tools/home/checkout'),
        permissions: ['view_wonderbar'],
    },
    {
        title: 'Copy Editor',
        keywords: ['copy', 'texts', 'translations', 'wording'],
        iconName: 'PenLine',
        path: accountPage('/engage/copy_editor/home/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Email Templates',
        keywords: ['emails', 'templates'],
        iconName: 'Mail',
        path: accountPage('/engage/email_templates/home/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Ticket Templates',
        keywords: ['ticket design', 'templates'],
        iconName: 'Ticket',
        path: accountPage('/engage/ticket_templates/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Gift Card Templates',
        keywords: ['gift cards', 'vouchers', 'templates'],
        iconName: 'Gift',
        path: accountPage('/engage/gift_card_templates/'),
        permissions: ['partner_admin'],
    },
]

const REPORT_PAGES: DirectoryPage[] = [
    {
        title: 'Reports',
        keywords: ['analytics', 'statistics'],
        iconName: 'ChartLine',
        path: accountPage('/reports/'),
        permissions: ['access_analytics'],
    },
    reportPage('Reports Overview', 'overview/', 'ReportsOverview', 'ChartLine', ['analytics overview']),
    reportPage('Sales Overview Report', 'sales-old', 'sale_reports', 'ChartSpline', ['sales', 'revenue']),
    reportPage('Sales & Finance Report', 'sales', 'SalesAndFinanceReport', 'ChartColumn', [
        'sales',
        'finance',
        'revenue',
    ]),
    // Sigma page deep links: the hash is the Sigma page nodeId, forwarded by
    // sigmaReport.tsx into the signed embed URL (page-level embed).
    reportPage('Sales & Finance Report · Sales', 'sales#uywwmBHTxq', 'SalesAndFinanceReport', 'ChartColumn', [
        'sales page',
        'sales report',
        'revenue',
    ]),
    reportPage('Sales & Finance Report · Pricing', 'sales#aMVEeJkjIJ', 'SalesAndFinanceReport', 'ChartColumn', [
        'pricing report',
        'price analysis',
        'prices',
    ]),
    reportPage('Sales & Finance Report · Transactions', 'sales#hxYdqB_d8n', 'SalesAndFinanceReport', 'ChartColumn', [
        'transactions',
        'payments report',
        'payouts',
    ]),
    reportPage('Sales & Finance Report · Tax', 'sales#svjGAVHjhs', 'SalesAndFinanceReport', 'ChartColumn', [
        'tax',
        'vat',
        'btw',
    ]),
    reportPage('Sales & Finance Report · Memberships', 'sales#-3v-INCVsa', 'SalesAndFinanceReport', 'ChartColumn', [
        'membership sales',
        'memberships report',
        'season passes',
    ]),
    reportPage('Sales & Finance Report · Fees', 'sales#P5-wvcKi_Q', 'SalesAndFinanceReport', 'ChartColumn', [
        'fees',
        'booking fees',
    ]),
    reportPage('Operations Report', 'operations', 'OperationsReport', 'ChartBar', ['operations', 'visits']),
    reportPage('Operations Report · Daily', 'operations#CdJ3ewFsA6', 'OperationsReport', 'ChartBar', [
        'daily operations',
        'daily visits',
        'daily report',
    ]),
    reportPage('Operations Report · Barcodes', 'operations#Rz0HGv13l_', 'OperationsReport', 'ChartBar', [
        'barcodes',
        'scans',
        'redemptions',
    ]),
    reportPage('Marketing Report', 'marketing', 'MarketingReport', 'ChartPie', ['marketing', 'campaigns']),
    reportPage('Marketing Report · Discount codes', 'marketing#e0OWVxSK0P', 'MarketingReport', 'ChartPie', [
        'discount codes',
        'coupons',
        'promo codes',
    ]),
    reportPage('Marketing Report · Engage tools', 'marketing#xLIbzyMYbB', 'MarketingReport', 'ChartPie', [
        'engage tools',
        'popups',
        'wonderbar',
    ]),
    reportPage('Marketing Report · Checkout performance', 'marketing#-C18gFCPnl', 'MarketingReport', 'ChartPie', [
        'checkout performance',
        'checkout funnel',
        'conversion',
    ]),
    reportPage('Marketing Report · Emails', 'marketing#5xCrsM0cH0', 'MarketingReport', 'ChartPie', [
        'emails report',
        'email performance',
        'open rate',
    ]),
    reportPage('Checkout Funnel Report', 'checkout_funnel/', 'CheckoutFunnelReport', 'Filter', [
        'funnel',
        'conversion',
        'checkout',
    ]),
    reportPage('Emails Report', 'emails/', 'EmailsReport', 'MailOpen', ['emails', 'open rate']),
    reportPage('Barcodes Report', 'barcodes/', 'BarcodesReport', 'Barcode', ['barcodes', 'redemption', 'scans']),
    reportPage('Discount Codes Report', 'discount_codes/', 'DiscountCodesReport', 'TicketPercent', [
        'discounts',
        'codes',
        'coupons',
    ]),
    reportPage('CRM Report', 'crm/', 'CRMReportPage', 'UsersRound', ['crm', 'customers', 'audience']),
    {
        ...reportPage('Engage Tools Report', 'engage_tools/', 'EngageToolsReport', 'Megaphone', [
            'engage',
            'wonderbar',
        ]),
        permissions: ['view_wonderbar'],
    },
]

const SETTINGS_PAGES: DirectoryPage[] = [
    {
        title: 'Account Settings',
        keywords: ['settings', 'payment methods', 'integrations', 'tax', 'booking fees'],
        iconName: 'Settings2',
        path: accountPage('/settings/account_settings/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Script',
        keywords: ['snippet', 'installation', 'tracking code', 'embed'],
        iconName: 'Code',
        path: accountPage('/settings/add_snippet/'),
        permissions: ['partner_admin'],
    },
    {
        title: 'Users',
        keywords: ['team', 'permissions', 'invite user'],
        iconName: 'Users',
        path: accountPage('/settings/users/user_list'),
        permissions: ['partner_admin'],
    },
]

const PERSONAL_PAGES: DirectoryPage[] = [
    {
        title: "What's New",
        keywords: ['release notes', 'updates', 'changelog'],
        iconName: 'Mailbox',
        path: accountPage('/release_notes/'),
        feature: 'release_notes_page',
        hideForReseller: true,
    },
    {
        title: 'Notifications',
        keywords: ['feed', 'alerts'],
        iconName: 'Bell',
        path: accountPage('/notifications/feed'),
        hideForReseller: true,
    },
    {
        title: 'Profile',
        keywords: ['personal settings', 'password', 'avatar'],
        iconName: 'UserPen',
        path: () => '/personal_settings/personal',
    },
    {
        title: 'Service apps',
        keywords: ['api key', 'api access', 'token', 'integration', 'automation', 'client credentials', 'mcp'],
        iconName: 'KeyRound',
        path: () => '/personal_settings/service_apps',
    },
]

// Commands: action screens reachable by name. Rows navigate to the action
// UI — the palette itself never mutates anything.
const COMMAND_PAGES: DirectoryPage[] = [
    {
        title: 'Redeem a barcode',
        keywords: ['redeem', 'redeemer', 'scan', 'check in', 'validate ticket'],
        iconName: 'ScanLine',
        path: accountPage('/orders/orders/redeemMode'),
        permissions: ['access_orders'],
        hint: 'Action',
    },
    {
        title: 'Redeem a group booking',
        keywords: ['redeem group', 'group check in', 'group redeem'],
        iconName: 'Users',
        path: accountPage('/orders/orders/groupRedeemMode'),
        permissions: ['access_orders'],
        hint: 'Action',
    },
    {
        title: 'Export orders',
        keywords: ['export', 'download orders', 'csv', 'excel'],
        iconName: 'Download',
        path: accountPage('/orders/orders/export'),
        permissions: ['access_orders'],
        hint: 'Action',
    },
]

export const PAGE_DIRECTORY: DirectoryPage[] = [
    ...DASHBOARD_PAGES,
    ...ORDER_PAGES,
    ...PRODUCT_PAGES,
    ...VENUE_PAGES,
    ...SALES_PAGES,
    ...ENGAGE_PAGES,
    ...REPORT_PAGES,
    ...SETTINGS_PAGES,
    ...PERSONAL_PAGES,
    ...COMMAND_PAGES,
]

export const MAX_PAGE_RESULTS = 5

// Title matches rank above keyword matches; prefix matches rank above substring.
const matchScore = (page: DirectoryPage, query: string): number => {
    const title = page.title.toLowerCase()
    if (title.startsWith(query)) {
        return 4
    }
    if (title.includes(query)) {
        return 3
    }
    if (page.keywords.some((keyword) => keyword.startsWith(query))) {
        return 2
    }
    if (page.keywords.some((keyword) => keyword.includes(query))) {
        return 1
    }
    return 0
}

export const searchPages = (
    query: string,
    accountSlug: string,
    canAccess: (page: DirectoryPage) => boolean,
): PageResult[] =>
    PAGE_DIRECTORY.map((page) => ({ page, score: matchScore(page, query.toLowerCase()) }))
        .filter(({ page, score }) => score > 0 && canAccess(page))
        .sort((a, b) => b.score - a.score)
        .slice(0, MAX_PAGE_RESULTS)
        .map(({ page }) => ({
            title: page.title,
            iconName: page.iconName,
            path: page.path(accountSlug),
            hint: page.hint,
        }))
